import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Theater, Sparkles, X, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { EMOTIONS, type EmotionId } from '../constants/emotions';
import { queryKeys } from '../lib/queryKeys';
import { discoverByEmotions } from '../api/tmdb';
import { MovieCard } from '../components/MovieCard';
import { useBackNavigation } from '../hooks/useBackNavigation';
import type { MediaItem } from '../types/movie';

// Default sort for /feel: high-rated movies first (evidence gated by popular gate v1: vote_count >= 50 OR popularity >= 4)
const DEFAULT_SORT = 'filmpl_rating.desc';
const DEFAULT_LIMIT = 24;

export function IFeelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const [selectedEmotions, setSelectedEmotions] = useState<EmotionId[]>([]);
  const [useFilmply, setUseFilmply] = useState(false);
  const [shake, setShake] = useState(false);
  // Track when user has searched to show results section
  const [hasSearched, setHasSearched] = useState(false);

  const MAX_EMOTIONS = 3;
  const isFilmplyAvailable = !!user; // For now, only logged-in users can use Filmply

  // Sorted emotions for stable query key
  const sortedEmotions = useMemo(
    () => [...selectedEmotions].sort(),
    [selectedEmotions]
  );

  // React Query for emotion-based discovery
  const {
    data,
    isLoading,
    isFetching,
    refetch,
    isError,
    error,
  } = useQuery({
    queryKey: queryKeys.feel.results(sortedEmotions, DEFAULT_SORT, DEFAULT_LIMIT),
    queryFn: () => discoverByEmotions(sortedEmotions, 1, DEFAULT_LIMIT, DEFAULT_SORT),
    enabled: false, // Manual trigger only
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (garbage collect)
  });

  // Apply Filmply AI rerank (client-side when toggle is on)
  // Prefers higher filmpl_rating + imdb_votes for better quality
  const results = useMemo(() => {
    if (!data?.results) return [];

    if (!useFilmply || !user) {
      return data.results;
    }

    // Filmply AI mode: rerank by quality signals
    // Primary: filmpl_rating (already sorted by this from backend)
    // Secondary: prefer items with higher imdb_votes (OMDB evidence); fallback to vote_count (TMDB)
    // Tertiary: stable sort by id
    return [...data.results].sort((a, b) => {
      const ratingA = a.filmpl_rating ?? a.imdb_rating ?? 0;
      const ratingB = b.filmpl_rating ?? b.imdb_rating ?? 0;

      // Primary: rating (only reorder if difference is meaningful)
      if (Math.abs(ratingA - ratingB) > 0.3) {
        return ratingB - ratingA;
      }

      // Secondary: prefer higher vote evidence (imdb_votes preferred, fallback vote_count)
      const votesA = (a as { imdb_votes?: number }).imdb_votes ?? a.vote_count ?? 0;
      const votesB = (b as { imdb_votes?: number }).imdb_votes ?? b.vote_count ?? 0;
      if (votesA !== votesB) {
        return votesB - votesA;
      }

      // Tertiary: stable sort by id
      return a.id - b.id;
    });
  }, [data?.results, useFilmply, user]);

  const handleEmotionClick = (emotionId: EmotionId) => {
    if (selectedEmotions.includes(emotionId)) {
      // Remove if already selected
      setSelectedEmotions(selectedEmotions.filter(e => e !== emotionId));
      setHasSearched(false); // Reset search state when selection changes
    } else if (selectedEmotions.length < MAX_EMOTIONS) {
      // Add if under limit
      setSelectedEmotions([...selectedEmotions, emotionId]);
      setHasSearched(false);
    } else {
      // Shake animation if trying to add more than max
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const removeEmotion = (emotionId: EmotionId) => {
    setSelectedEmotions(selectedEmotions.filter(e => e !== emotionId));
    setHasSearched(false);
  };

  const clearAllEmotions = () => {
    setSelectedEmotions([]);
    setHasSearched(false);
  };

  const handleFindMovies = useCallback(async () => {
    if (selectedEmotions.length === 0) return;
    setHasSearched(true);
    await refetch();
  }, [selectedEmotions, refetch]);

  const handleFilmplyToggle = () => {
    if (!isFilmplyAvailable && !useFilmply) {
      // Show upgrade prompt for non-logged-in users (could navigate to login)
      return;
    }
    setUseFilmply(!useFilmply);
  };

  const handleMovieClick = (movie: MediaItem) => {
    const mediaType = movie.media_type || 'movie';
    navigate(`/${mediaType}/${movie.id}`);
  };

  // Skeleton grid component
  const SkeletonGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={`skeleton-${i}`} className="group">
          <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26]">
            <div className="w-full h-full skeleton" />
          </div>
          <div className="mt-2 px-1 space-y-1">
            <div className="h-4 skeleton rounded w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#111111] py-12">
      <div className="max-w-6xl mx-auto px-6">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Theater size={40} className="text-teal-400" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              What do you want to feel?
            </h1>
          </div>
          <p className="text-slate-400 text-lg">
            Choose up to {MAX_EMOTIONS} moods and we'll find movies that evoke them
          </p>
        </div>

        {/* Filmply/Basic Toggle */}
        <div className="flex justify-center mb-10">
          <div className="flex bg-[#1A1F26] rounded-lg p-1">
            <button
              onClick={() => setUseFilmply(false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                !useFilmply
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Basic
            </button>
            <button
              onClick={handleFilmplyToggle}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                useFilmply
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                  : isFilmplyAvailable
                    ? 'text-slate-400 hover:text-white'
                    : 'text-slate-500 cursor-not-allowed'
              }`}
              disabled={!isFilmplyAvailable && !useFilmply}
            >
              <Sparkles size={16} />
              Filmply
              {!isFilmplyAvailable && (
                <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded">PRO</span>
              )}
            </button>
          </div>
        </div>

        {/* Not logged in prompt */}
        {!isFilmplyAvailable && (
          <div className="text-center mb-8 p-4 bg-[#1A1F26]/50 rounded-xl ring-1 ring-white/[0.08]">
            <p className="text-slate-400 text-sm">
              <Sparkles size={14} className="inline mr-1 text-teal-400" />
              Log in to unlock Filmply for personalized recommendations
            </p>
          </div>
        )}

        {/* Emotion Grid - 4x4 */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 ${
            shake ? 'animate-shake' : ''
          }`}
        >
          {EMOTIONS.map((emotion) => {
            const isSelected = selectedEmotions.includes(emotion.id);
            return (
              <button
                key={emotion.id}
                onClick={() => handleEmotionClick(emotion.id)}
                className={`flex flex-col items-center gap-2 px-4 py-4 rounded-xl transition-all duration-200 ${
                  isSelected
                    ? 'border-2 border-teal-500 bg-teal-500/10 scale-105'
                    : 'bg-[#1A1F26] border-2 border-transparent hover:bg-slate-700 hover:scale-105'
                }`}
              >
                <span className="text-2xl">{emotion.icon}</span>
                <span className={`text-sm font-medium ${
                  isSelected ? 'text-teal-400' : 'text-white'
                }`}>
                  {emotion.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Selected Chips */}
        {selectedEmotions.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {selectedEmotions.map((emotionId) => {
              const emotion = EMOTIONS.find(e => e.id === emotionId);
              if (!emotion) return null;
              return (
                <div
                  key={emotionId}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 border border-teal-500 rounded-full"
                >
                  <span className="text-sm">{emotion.icon}</span>
                  <span className="text-sm text-teal-400 font-medium">{emotion.label}</span>
                  <button
                    onClick={() => removeEmotion(emotionId)}
                    className="text-teal-400 hover:text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
            <button
              onClick={clearAllEmotions}
              className="text-xs text-slate-500 hover:text-slate-400 underline ml-2"
            >
              Clear all
            </button>
            <p className="w-full text-center text-xs text-slate-500 mt-2">
              {selectedEmotions.length}/{MAX_EMOTIONS} selected
            </p>
          </div>
        )}

        {/* Find Movies Button */}
        <div className="text-center mb-12">
          <button
            onClick={handleFindMovies}
            disabled={selectedEmotions.length === 0 || isFetching}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-lg text-lg font-semibold transition-all ${
              selectedEmotions.length > 0 && !isFetching
                ? 'bg-teal-500 text-white hover:bg-teal-600 hover:scale-105'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
          >
            {isFetching ? (
              <>
                <RefreshCw size={20} className="animate-spin" />
                Finding...
              </>
            ) : (
              <>
                <span>🎬</span>
                Find Movies
              </>
            )}
          </button>
        </div>

        {/* Results Section */}
        {hasSearched && (
          <div className="mt-8">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {isLoading || isFetching ? (
                  'Finding movies...'
                ) : results.length > 0 ? (
                  <>
                    Movies that feel{' '}
                    <span className="text-teal-400">
                      {selectedEmotions
                        .map(id => EMOTIONS.find(e => e.id === id)?.label)
                        .filter(Boolean)
                        .join(' + ')}
                    </span>
                  </>
                ) : (
                  'No matches found'
                )}
              </h2>
              {results.length > 0 && (
                <span className="text-sm text-slate-500">
                  {data?.total_results ?? 0} results
                </span>
              )}
            </div>

            {/* Error State */}
            {isError && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">😞</div>
                <h3 className="text-xl font-semibold text-white mb-2">Something went wrong</h3>
                <p className="text-slate-400 mb-4">
                  {error instanceof Error ? error.message : 'Failed to find movies'}
                </p>
                <button
                  onClick={() => refetch()}
                  className="text-teal-400 hover:text-teal-300 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Loading State - Skeleton Grid */}
            {(isLoading || isFetching) && !isError && <SkeletonGrid />}

            {/* Results Grid */}
            {!isLoading && !isFetching && !isError && results.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {results.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    onClick={handleMovieClick}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !isFetching && !isError && results.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎭</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  No movies found for this mood combo
                </h3>
                <p className="text-slate-400 mb-6">
                  Try selecting different emotions or fewer emotions for more results.
                </p>
                <p className="text-xs text-slate-500">
                  Powered by Filmply emotion tags
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info Footer (shown when no search yet) */}
        {!hasSearched && (
          <div className="text-center mt-12 text-xs text-slate-600">
            Powered by Filmply emotion tags
          </div>
        )}

        {/* Shake animation keyframes */}
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
            20%, 40%, 60%, 80% { transform: translateX(4px); }
          }
          .animate-shake {
            animation: shake 0.5s ease-in-out;
          }
          .skeleton {
            background: linear-gradient(90deg, #1A1F26 25%, #2A2F36 50%, #1A1F26 75%);
            background-size: 200% 100%;
            animation: skeleton-shimmer 1.5s infinite;
          }
          @keyframes skeleton-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    </div>
  );
}
